import api from "./api";

export const COFFEEBEAN_TARGET_URL = "https://coffeebean.in";

interface CrawlOptionsPayload {
  maxPages?: number;
  maxDepth?: number;
}

export interface WebsiteContentSummary {
  id: string;
  url: string | null;
  title: string | null;
  textLength: number;
  chunkCount: number;
  checksum: string;
  isManual: boolean;
  manuallyEdited: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WebsiteContentDetail {
  id: string;
  url: string | null;
  title: string | null;
  text: string;
  checksum: string;
  isManual: boolean;
  manuallyEdited: boolean;
  createdAt: string;
  updatedAt: string;
  chunks: {
    id: string;
    chunkText: string;
    createdAt: string;
  }[];
}

export interface ManualContentInput {
  url?: string; // Optional - for manual entries without URLs
  title?: string;
  text: string;
}

export interface WebsiteContentListResponse {
  success: boolean;
  data: {
    contents: WebsiteContentSummary[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export interface WebsiteContentStatsResponse {
  success: boolean;
  data: {
    totalContents: number;
    totalChunks: number;
    recentContents: {
      id: string;
      url: string;
      title: string | null;
      updatedAt: string;
    }[];
  };
}

export interface WebsiteSearchResult {
  chunkId: string;
  contentId: string;
  url: string;
  title?: string | null;
  chunkText: string;
  similarity: number;
}

export interface WebsiteSearchResponse {
  success: boolean;
  data: {
    results: WebsiteSearchResult[];
    query: string;
    count: number;
  };
}

export const websiteContentApi = {
  /**
   * Get all website content with pagination
   */
  getAll: async (page = 1, pageSize = 20, search = "", filter = "all"): Promise<WebsiteContentListResponse> => {
    const response = await api.get<WebsiteContentListResponse>("/api/website-content", {
      params: { page, pageSize, search, filter },
    });
    return response.data;
  },

  /**
   * Get a single website content with full details
   */
  getById: async (id: string): Promise<{ success: boolean; data: WebsiteContentDetail }> => {
    const response = await api.get<{ success: boolean; data: WebsiteContentDetail }>(
      `/api/website-content/${id}`,
    );
    return response.data;
  },

  /**
   * Trigger a crawl starting from the Coffee Bean root URL
   */
  crawlSite: async (
    options: CrawlOptionsPayload = {},
  ): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.post<{ success: boolean; message: string; data?: any }>(
      "/api/website-content/crawl",
      {
        url: COFFEEBEAN_TARGET_URL,
        ...options,
      },
      {
        // Crawling can take several minutes, so we allow up to 5 minutes
        timeout: 300000,
      },
    );
    return response.data;
  },

  /**
   * Trigger a full website crawl
   */
  crawlAll: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      "/api/website-content/crawl",
      {},
      {
        // Full crawl runs in background but initial response may take time
        timeout: 30000,
      },
    );
    return response.data;
  },

  /**
   * Delete a website content entry
   */
  deleteById: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete<{ success: boolean; message: string }>(
      `/api/website-content/${id}`,
    );
    return response.data;
  },

  /**
   * Delete all website content
   */
  deleteAll: async (): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.delete<{ success: boolean; message: string; data?: any }>(
      "/api/website-content",
    );
    return response.data;
  },

  /**
   * Get overview statistics
   */
  getStats: async (): Promise<WebsiteContentStatsResponse> => {
    const response = await api.get<WebsiteContentStatsResponse>(
      "/api/website-content/stats/overview",
    );
    return response.data;
  },

  /**
   * Check system health (OpenAI API, database, etc.)
   */
  checkHealth: async (): Promise<{ success: boolean; data: any }> => {
    const response = await api.get<{ success: boolean; data: any }>(
      "/api/website-content/health",
    );
    return response.data;
  },

  /**
   * Search website content using vector similarity
   */
  search: async (
    query: string,
    limit: number = 10,
    minSimilarity: number = 0.5,
  ): Promise<WebsiteSearchResponse> => {
    const response = await api.post<WebsiteSearchResponse>("/api/website-content/search", {
      query,
      limit,
      minSimilarity,
    });
    return response.data;
  },

  /**
   * Create a manual website content entry
   */
  createManual: async (
    input: ManualContentInput,
  ): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.post<{ success: boolean; message: string; data?: any }>(
      "/api/website-content/manual",
      input,
    );
    return response.data;
  },

  /**
   * Update an existing website content entry
   */
  update: async (
    id: string,
    input: ManualContentInput,
  ): Promise<{ success: boolean; message: string; data?: any }> => {
    const response = await api.put<{ success: boolean; message: string; data?: any }>(
      `/api/website-content/${id}`,
      input,
    );
    return response.data;
  },
};

export default websiteContentApi;
